import { useTranslation } from 'react-i18next'
import { BellOff, BellRing, ChevronRight, Download, Info, Smartphone } from 'lucide-react'
import { enablePush, promptInstall, usePushState } from '@/lib/push/pushStore'
import { openPushPrompt } from '@/lib/push/events'
import { cn } from '@/lib/utils'

/**
 * F-186: in the avatar panel, a standing line while push is unresolved on this
 * device ("Instale o app", "Notificações desligadas · Ativar", blocked, or why
 * this browser cannot). It stays until the state is resolved; nothing to
 * dismiss. `onAction` closes the panel first.
 */
export const PushPanelItem = ({ onAction }: { onAction: () => void }) => {
  const { t } = useTranslation('push')
  const push = usePushState()
  if (push.phase === 'off' || push.phase === 'done') return null

  const unsupported = push.phase === 'unsupported'
  const label = unsupported ? t(`menu.${push.unsupported ?? 'browser'}`) : t(`menu.${push.phase}`)
  const Icon = unsupported ? Info : push.phase === 'blocked' ? BellOff : push.phase === 'enable' ? BellRing : push.phase === 'install' ? Download : Smartphone

  const run = () => {
    /* `enablePush` asks the browser first thing, inside this click. */
    if (push.phase === 'enable') { void enablePush(); onAction(); return }
    if (push.phase === 'install' && push.canPromptInstall) { void promptInstall(); onAction(); return }
    if (push.phase === 'install' || push.phase === 'openInstalled' || push.phase === 'blocked') { onAction(); openPushPrompt(push.phase) }
  }

  const className = cn(
    'flex w-full items-center gap-2 border-b border-gray-200 px-3 py-2 text-left text-sm sm:px-4',
    push.phase === 'blocked' || unsupported ? 'bg-amber-50 text-amber-900' : 'bg-primary/5 text-gray-800'
  )

  if (unsupported) {
    return (
      <p className={className} data-push-panel-item="unsupported">
        <Icon className="h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden />
        <span className="min-w-0 flex-1">{label}</span>
      </p>
    )
  }
  return (
    <button type="button" className={cn(className, 'hover:bg-primary/10')} onClick={run} disabled={push.busy} data-push-panel-item={push.phase}>
      <Icon className={cn('h-4 w-4 flex-shrink-0', push.phase === 'blocked' ? 'text-amber-600' : 'text-primary')} aria-hidden />
      <span className="min-w-0 flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden />
    </button>
  )
}
